import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdmissionEnquiryForm from '../components/common/AdmissionEnquiryForm';
import { apiService } from '../services/api';
import { getImageUrlForStyle, getImageUrl } from '../utils/imageUtils';

const StandardHomePage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [homepageData, setHomepageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  useEffect(() => {
    fetchHomepageData();
    fetchAnnouncements();
  }, []);

  const fetchHomepageData = async () => {
    try {
      const response = await apiService.getHomepage();
      setHomepageData(response.data);
    } catch (error) {
      console.error('Error fetching homepage data:', error);
      // Keep default data if API fails
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      setAnnouncementsLoading(true);
      
      const response = await apiService.getAnnouncements({
        active: 'true',
        limit: 6,
        page: 1
      });
      
      if (response && response.success && response.data) {
        const activeAnnouncements = response.data.announcements || [];
        
        // Filter only active announcements and sort by pinned first, then by date
        const sortedAnnouncements = activeAnnouncements
          .filter(ann => ann.isActive === true)
          .sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return new Date(b.publishedAt) - new Date(a.publishedAt);
          });
        
        setAnnouncements(sortedAnnouncements);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  // Default data for fallback
  const defaultData = {
    heroTitle: 'FiT Engineering College',
    heroSubtitle: 'Building Tomorrow\'s Engineers Today',
    heroDescription: 'Shaping the future through excellence in engineering education, innovation, and research since our establishment.',
    quickStats: [
      { number: '50+', label: 'Academic Programs' },
      { number: '5000+', label: 'Students' },
      { number: '200+', label: 'Faculty Members' },
      { number: '90%+', label: 'Placement Rate' }
    ],
    whyChooseTitle: 'Why Choose FiT Engineering College?',
    whyChooseDescription: 'With decades of excellence in engineering education, we provide world-class facilities, expert faculty, and industry connections that prepare our students for successful careers.',
    whyChooseFeatures: [
      'State-of-the-art laboratories and workshops',
      'Industry partnerships and internship programs',
      'Expert faculty with real-world experience',
      'Strong alumni network across global companies',
      '100% placement assistance for eligible students'
    ],
    ctaTitle: 'Ready to Start Your Journey?',
    ctaDescription: 'Join thousands of successful graduates who started their careers at FiT Engineering College. Your future begins here.'
  };

  // Use dynamic data if available, otherwise use defaults
  const currentData = homepageData || defaultData;
  const quickStats = currentData.quickStats || defaultData.quickStats;

  const defaultFeaturedPrograms = [
    {
      title: 'Computer Science & Engineering',
      description: 'Cutting-edge curriculum in AI, Machine Learning, and Software Development',
      icon: 'fas fa-laptop-code',
      link: '/academics'
    },
    {
      title: 'Mechanical Engineering',
      description: 'Advanced manufacturing, robotics, and automotive engineering programs',
      icon: 'fas fa-cogs',
      link: '/academics'
    },
    {
      title: 'Electronics & Communication',
      description: 'Modern electronics, IoT, and communication systems technology',
      icon: 'fas fa-microchip',
      link: '/academics'
    },
    {
      title: 'Civil Engineering',
      description: 'Infrastructure development and sustainable construction practices',
      icon: 'fas fa-building',
      link: '/academics'
    }
  ];
  
  const featuredPrograms = currentData.featuredPrograms || defaultFeaturedPrograms;

  const defaultNewsUpdates = [
    {
      date: 'Dec 1, 2024',
      title: 'Admissions Open for Academic Year 2025-26',
      description: 'Apply now for undergraduate and postgraduate programs',
      link: '/admission'
    },
    {
      date: 'Nov 28, 2024',
      title: 'Research Excellence Award 2024',
      description: 'FiT Engineering College receives recognition for outstanding research contributions',
      link: '/research'
    }
  ];
  
  const newsUpdates = currentData.newsUpdates || defaultNewsUpdates;

  return (
    <div className="h-full w-full  fade-in">
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center min-h-[500px] md:h-96 py-8 md:py-0"
        style={{
          backgroundImage: currentData.heroBackgroundImage 
            ? getImageUrlForStyle(currentData.heroBackgroundImage)
            : "url('https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')"
        }}
      >
        <div className="hero-overlay absolute inset-0"></div>
        
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 w-full">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="text-white text-center md:text-left">
                <h1 className="font-sans font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-4 md:mb-6">
                  {currentData.heroSubtitle || 'Welcome to'}<br />
                  <span className="text-gold">{currentData.heroTitle || 'FiT Engineering College'}</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl mb-6 md:mb-8 opacity-90 leading-relaxed">
                  {currentData.heroDescription || 'Shaping the future through excellence in engineering education, innovation, and research since our establishment.'}
                </p>
                <div className="flex flex-col gap-4 items-center md:items-start">
                  <div className="flex flex-row gap-4 justify-center md:justify-start">
                    <button
                      onClick={openForm}
                      className="bg-crimson hover:bg-red-800 text-white font-bold py-2 px-4 rounded-lg transition transform hover:-translate-y-1 shadow-lg text-sm inline-block"
                    >
                      Admission Enquiry
                    </button>
                    <Link
                      to="/academics"
                      className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate font-bold py-2 px-4 rounded-lg transition text-sm inline-block"
                    >
                      Academic Programs
                    </Link>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                    <a
                      href="https://uptac.samarth.edu.in/index.php"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gold hover:bg-yellow-600 text-slate font-bold py-2 px-4 rounded-lg transition transform hover:-translate-y-1 shadow-lg text-sm inline-block text-center"
                    >
                      Registration via UPTAC (B.Tech) Counselling
                    </a>
                    <a
                      href="https://jeecup.admissions.nic.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gold hover:bg-yellow-600 text-slate font-bold py-2 px-4 rounded-lg transition transform hover:-translate-y-1 shadow-lg text-sm inline-block text-center"
                    >
                      Registration via JEECUP (Polytechnic) Counselling
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Announcements Card */}
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border-2 border-gray-200 w-full">
                <h3 className="font-sans font-bold text-2xl text-slate mb-4">
                  Announcements 
                  <small className="text-sm text-gray-500 ml-2">({announcements.length} found)</small>
                </h3>
                <div className="h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  <div className="space-y-3">
                    {announcementsLoading ? (
                      <div className="flex items-center justify-center h-full">
                        <i className="fas fa-spinner fa-spin text-2xl text-crimson"></i>
                      </div>
                    ) : announcements.length > 0 ? (
                      announcements.map((announcement) => {
                        const categoryColors = {
                          admission: 'border-crimson text-crimson',
                          examination: 'border-blue-500 text-blue-500',
                          event: 'border-green-500 text-green-500',
                          scholarship: 'border-orange-500 text-orange-500',
                          placement: 'border-purple-500 text-purple-500',
                          fee: 'border-red-500 text-red-500',
                          result: 'border-indigo-500 text-indigo-500',
                          workshop: 'border-teal-500 text-teal-500',
                          holiday: 'border-yellow-500 text-yellow-500',
                          sports: 'border-pink-500 text-pink-500'
                        };
                        
                        const categoryIcons = {
                          admission: 'fas fa-bullhorn',
                          examination: 'fas fa-file-alt',
                          event: 'fas fa-calendar',
                          scholarship: 'fas fa-award',
                          placement: 'fas fa-building',
                          fee: 'fas fa-exclamation-triangle',
                          result: 'fas fa-graduation-cap',
                          workshop: 'fas fa-robot',
                          holiday: 'fas fa-umbrella-beach',
                          sports: 'fas fa-trophy'
                        };
                        
                        const colorClass = categoryColors[announcement.category] || 'border-gray-500 text-gray-500';
                        const iconClass = categoryIcons[announcement.category] || 'fas fa-info-circle';
                        const borderColor = colorClass.split(' ')[0];
                        const textColor = colorClass.split(' ')[1];
                        
                        const formatDate = (dateString) => {
                          return new Date(dateString).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          });
                        };
                        
                        return (
                          <div key={announcement.id} className={`p-3 bg-gray-50 rounded-lg border-l-4 ${borderColor} relative hover:bg-gray-100 transition-colors`}>
                            {announcement.isPinned && (
                              <i className="fas fa-thumbtack text-crimson absolute top-2 right-2 text-xs" title="Pinned"></i>
                            )}
                            <div className="flex items-start gap-3">
                              <i className={`${iconClass} ${textColor} mt-1`}></i>
                              <div className="flex-1">
                                <h4 className="font-semibold text-slate text-sm pr-4">{announcement.title}</h4>
                                <p className="text-xs text-gray-500 mt-1">{announcement.description}</p>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className={`text-xs ${textColor}`}>{formatDate(announcement.publishedAt)}</span>
                                  {announcement.attachments && announcement.attachments.length > 0 && (
                                    <div className="flex items-center gap-1">
                                      {announcement.attachments.map((att, idx) => {
                                        const url = att.url ? (att.url.startsWith('http') ? att.url : `${apiService.baseURL.replace(/\/api$/, '')}${att.url}`) : '#';
                                        const isPdf = (att.mimeType || '').includes('pdf') || (att.originalName || '').toLowerCase().endsWith('.pdf');
                                        const icon = isPdf ? 'fas fa-file-pdf text-red-500' : 'fas fa-paperclip text-gray-500';
                                        return (
                                          <a
                                            key={idx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-slate hover:bg-crimson hover:text-white hover:border-crimson transition-colors"
                                            title={att.originalName || att.filename || 'Attachment'}
                                          >
                                            <i className={icon} style={{fontSize: '10px'}}></i>
                                            <span>View</span>
                                          </a>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <i className="fas fa-info-circle text-3xl mb-2"></i>
                        <p className="text-sm">No active announcements</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <Link to="/announcements" className="text-crimson text-sm font-semibold hover:underline">
                    View All Announcements <i className="fas fa-arrow-right ml-1"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {quickStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-crimson mb-1 sm:mb-2">{stat.number}</div>
                <div className="text-xs sm:text-sm md:text-base text-gray-600 font-semibold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      <section className="py-12 sm:py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-sans font-bold text-slate mb-3 sm:mb-4">Featured Programs</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto px-4 sm:px-0">
              Discover our industry-leading engineering programs designed to prepare you for tomorrow's challenges.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {featuredPrograms.map((program, index) => (
              <Link 
                key={index}
                to={program.link}
                className="bg-white p-6 rounded-xl border border-border_gray hover:shadow-card transition group"
              >
                <div className="w-16 h-16 bg-crimson rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-800 transition">
                  <i className={`${program.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate mb-3">{program.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{program.description}</p>
                <div className="mt-4 text-crimson font-semibold text-sm">
                  Learn More <i className="fas fa-arrow-right ml-1"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose FIT */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-sans font-bold text-slate mb-4 sm:mb-6 text-center md:text-left">{currentData.whyChooseTitle || defaultData.whyChooseTitle}</h2>
              <p className="text-gray-600 mb-6">{currentData.whyChooseDescription || defaultData.whyChooseDescription}</p>
              <div className="space-y-6">
                {(currentData.whyChooseFeatures || []).map((feature, index) => {
                  // Handle both string and object formats for backward compatibility
                  const isString = typeof feature === 'string';
                  const icon = isString ? 'fas fa-check-circle' : (feature.icon || 'fas fa-check-circle');
                  const heading = isString ? feature : (feature.heading || '');
                  const description = isString ? '' : (feature.description || '');
                  
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gold rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${icon} text-white`}></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate mb-2">{heading}</h3>
                        {description && <p className="text-gray-600">{description}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="order-first md:order-last">
              {currentData.whyChooseImage ? (
                <img 
                  src={getImageUrl(currentData.whyChooseImage)} 
                  alt="Why Choose FiT Engineering"
                  className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-xl"
                  onError={(e) => {
                    console.error('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                  onLoad={(e) => {
                    console.log('Image loaded successfully:', e.target.src);
                  }}
                />
              ) : (
                <div className="bg-gray-100 p-6 sm:p-8 rounded-xl h-64 sm:h-80 md:h-96 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <i className="fas fa-camera text-6xl mb-4"></i>
                    <p className="text-lg font-medium">Campus Virtual Tour</p>
                    <p className="text-sm">Experience our world-class facilities</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Important Links */}
      {currentData.importantLinks && currentData.importantLinks.length > 0 && (
        <section className="py-12 sm:py-16 bg-surface">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="grid md:grid-cols-1 gap-8 md:gap-12">
              <div>
                <h2 className="text-2xl sm:text-3xl font-sans font-bold text-slate mb-6 sm:mb-8">Important Links</h2>
                <div className="grid gap-4">
                  {currentData.importantLinks.map((linkItem, index) => {
                    const isExternal = linkItem.external;
                    const LinkComponent = isExternal ? 'a' : Link;
                    const linkProps = isExternal 
                      ? { href: linkItem.link, target: '_blank', rel: 'noopener noreferrer' }
                      : { to: linkItem.link };
                    
                    return (
                      <LinkComponent
                        key={index}
                        {...linkProps}
                        className="bg-white p-4 rounded-lg border border-border_gray hover:shadow-card transition flex items-center gap-3"
                      >
                        <i className={`${linkItem.icon} text-crimson`}></i>
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate">{linkItem.title}</h4>
                          <p className="text-sm text-gray-500">{linkItem.description}</p>
                        </div>
                        {isExternal && (
                          <i className="fas fa-external-link-alt text-gray-400"></i>
                        )}
                      </LinkComponent>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="py-12 sm:py-16 bg-slate text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-sans font-bold mb-4 sm:mb-6">Ready to Start Your Journey?</h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto px-4 sm:px-0">
            Join thousands of successful graduates who started their careers at FiT Engineering College. 
            Your future begins here.
          </p>
          <div className="flex flex-row gap-4 justify-center">
            <button 
              onClick={openForm}
              className="bg-crimson hover:bg-red-800 text-white font-bold py-2 px-4 rounded-lg transition transform hover:-translate-y-1 shadow-lg text-sm inline-block"
            >
              Admission Enquiry
            </button>
            <Link 
              to="/about" 
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate font-bold py-2 px-4 rounded-lg transition text-sm inline-block"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>

      <AdmissionEnquiryForm isOpen={isFormOpen} onClose={closeForm} />
    </div>
  );
};

export default StandardHomePage;